import { useEffect, useState } from "react";
import axios from "axios";
import OnboardingShell from "./components/OnboardingShell";
import { API_BASE_URL } from "./config";

function Step3Payment({ onNext, onBack }) {

  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");

  const PRICE_MAP = {
    videosage: 50,
    audiosage: 30,
    screensage: 40,
    codesage: 60,
    aibuddy_basic: 4500,
    aibuddy_premium: 6500,
    visionix_ai: 1,
    face_recognition: 1,
    people_connect_ai: 5000,
    vrm_ai_estate: 50,
    exit_intelligence: 50
  };

  const PRODUCT_LABELS = {
    videosage: "VideoSage",
    audiosage: "AudioSage",
    screensage: "ScreenSage",
    codesage: "CodeSage",
    aibuddy_basic: "AI Buddy Basic",
    aibuddy_premium: "AI Buddy Premium",
    visionix_ai: "Visionix AI",
    face_recognition: "Visionix AI",
    people_connect_ai: "People Connect AI",
    vrm_ai_estate: "VRM AI Estate",
    exit_intelligence: "Exit Intelligence"
  };

  useEffect(() => {
    const step2Data = localStorage.getItem("step2Data");
    if (step2Data) setPurchase(JSON.parse(step2Data));
    setLoading(false);

    const resetPaying = () => setPaying(false);
    window.addEventListener("pageshow", resetPaying);
    window.addEventListener("focus", resetPaying);
    return () => {
      window.removeEventListener("pageshow", resetPaying);
      window.removeEventListener("focus", resetPaying);
    };
  }, []);

  const shell = (children) => (
    <OnboardingShell step={3} title="Payment details" wide>
      {children}
    </OnboardingShell>
  );

  if (loading) return shell(<p className="onboard-subtext">Loading payment details…</p>);

  if (!purchase) {
    return shell(
      <>
        <p className="onboard-error">Purchase details not found. Please go back and complete step 2.</p>
        <div className="button-row">
          <button className="onboard-button secondary" onClick={onBack} type="button">Back</button>
        </div>
      </>
    );
  }

  const selectedProducts = purchase.selectedProducts || {};
  const productKeys = Object.keys(selectedProducts);

  const totalAmount = productKeys.reduce((acc, key) => {
    const qty = parseInt(selectedProducts[key]) || 0;
    const pricePerInterview = PRICE_MAP[key] || 0;
    return acc + qty * pricePerInterview;
  }, 0);

  const handlePayNow = async () => {
    try {
      setPaying(true);
      setPayError("");

      const step1 = JSON.parse(localStorage.getItem("step1Data") || "{}");
      const step2 = JSON.parse(localStorage.getItem("step2Data") || "{}");

      const productNames = productKeys.map((k) => PRODUCT_LABELS[k]).join(", ");
      const totalCredits = productKeys.reduce(
        (acc, key) => acc + (parseInt(selectedProducts[key]) || 0),
        0
      );

      const goLive = (step2.goLiveDate && typeof step2.goLiveDate === 'string' && step2.goLiveDate.trim()) 
        ? step2.goLiveDate.trim() 
        : null;

      const payload = {
        company: {
          ...step1,
          agreementAccepted: true,
          agreementSignature: step1.signature || step2.signature || "",
          productCategory: step2.category || "workflow_ai",
          expectedGoLiveDate: goLive
        },
        purchase: {
          productName: productNames,
          credits: totalCredits,
          subscriptionType: step2.subscription || "",
          expectedGoLiveDate: goLive
        },
        amount: totalAmount
      };

      const res = await axios.post(
        `${API_BASE_URL}/api/payment/initiate`,
        payload
      );

      const { encRequest, accessCode, ccavenueUrl } = res.data;

      if (!encRequest || !ccavenueUrl) {
        throw new Error("Payment gateway did not return the expected data");
      }

      // Build a hidden form and auto-submit it — this is a real
      // full-page POST navigation to CCAvenue's hosted payment page,
      // so no CORS issues and no JS needed on their end.
      const form = document.createElement("form");
      form.method = "POST";
      form.action = ccavenueUrl;

      const encField = document.createElement("input");
      encField.type = "hidden";
      encField.name = "encRequest";
      encField.value = encRequest;
      form.appendChild(encField);

      const accessField = document.createElement("input");
      accessField.type = "hidden";
      accessField.name = "access_code";
      accessField.value = accessCode;
      form.appendChild(accessField);

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error(err);
      const serverMsg = err.response?.data?.error || err.message;
      setPayError(serverMsg ? `Couldn't start payment: ${serverMsg}` : "Couldn't start the payment. Please try again.");
      setPaying(false);
    }
  };

  return (
    <OnboardingShell
      step={3}
      title="Payment details"
      subtitle="Complete the transfer using the bank details below, then upload your receipt in the next step."
      wide
    >
      <div className="panel">
        <div className="panel-title">Payment summary</div>
        {productKeys.map((key, index) => {
          const qty = parseInt(selectedProducts[key]) || 0;
          const pricePerInterview = PRICE_MAP[key] || 0;

          return (
            <div key={key} style={{ marginBottom: index !== productKeys.length - 1 ? '16px' : '0', paddingBottom: index !== productKeys.length - 1 ? '16px' : '0', borderBottom: index !== productKeys.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div className="panel-row">
                <span className="panel-row-label">Product</span>
                <span className="panel-row-value">{PRODUCT_LABELS[key]}</span>
              </div>
              <div className="panel-row">
                <span className="panel-row-label">
                  {key.startsWith('aibuddy') || key === 'visionix_ai' || key === 'face_recognition' ? "Number of months" : "Number of interviews"}
                </span>
                <span className="panel-row-value">{qty}</span>
              </div>
              <div className="panel-row">
                <span className="panel-row-label">
                  {key.startsWith('aibuddy') || key === 'visionix_ai' || key === 'face_recognition' ? "Price per month" : "Price per interview"}
                </span>
                <span className="panel-row-value">₹{pricePerInterview}</span>
              </div>
            </div>
          );
        })}
        <div className="panel-row panel-total" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
          <span className="panel-row-label">Total amount</span>
          <span className="panel-row-value">₹{totalAmount.toLocaleString("en-IN")}</span>
        </div>
      </div>

      <div className="panel panel--outline">
        <div className="panel-title">Bank transfer details (manual, optional)</div>
        <div className="panel-row">
          <span className="panel-row-label">Customer ID</span>
          <span className="panel-row-value">41643466</span>
        </div>
        <div className="panel-row">
          <span className="panel-row-label">Account name</span>
          <span className="panel-row-value">VRM AI TECHNOLOGY (OPC) PVT. LTD.</span>
        </div>
        <div className="panel-row">
          <span className="panel-row-label">Account number</span>
          <span className="panel-row-value">108263200000102</span>
        </div>
        <div className="panel-row">
          <span className="panel-row-label">IFSC code</span>
          <span className="panel-row-value">YESB0001082</span>
        </div>
        <div className="panel-row">
          <span className="panel-row-label">Branch</span>
          <span className="panel-row-value">Electronic City</span>
        </div>
        <div className="panel-row">
          <span className="panel-row-label">UPI ID</span>
          <span className="panel-row-value">workflowai@hdfc</span>
        </div>
      </div>

      {payError && <p className="onboard-error" style={{ marginTop: 12 }}>{payError}</p>}

      <div className="button-row">
        <button className="onboard-button secondary" onClick={onBack} disabled={paying} type="button">Back</button>
        <button
          className="onboard-button onboard-button--primary"
          onClick={handlePayNow}
          disabled={paying}
          type="button"
        >
          {paying ? "Redirecting to payment…" : `Pay ₹${totalAmount.toLocaleString("en-IN")} Now`}
        </button>
      </div>
    </OnboardingShell>
  );
}

export default Step3Payment;